import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useTheme, alpha } from '@mui/material/styles';

interface SectionEntry {
  index: number;
  question: string;
  partName?: string;
}

interface VersionEntry {
  id: string;
  version: number;
  status: string;
  createdAt: string;
}

interface SidebarTrackerProps {
  sections: SectionEntry[];
  versions: VersionEntry[];
  onSectionClick: (index: number) => void;
}

const SidebarTracker: React.FC<SidebarTrackerProps> = ({ sections, versions, onSectionClick }) => {
  const [tab, setTab] = React.useState(0);
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        width: 250,
        flexShrink: 0,
        alignSelf: 'flex-start',
        position: 'sticky',
        top: 16,
        display: { xs: 'none', md: 'block' },
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        overflow: 'hidden',
      }}
    >
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="fullWidth"
        sx={{
          minHeight: 40,
          bgcolor: alpha(theme.palette.primary.main, 0.04),
          '& .MuiTab-root': { minHeight: 40, fontSize: '0.75rem', fontWeight: 600, textTransform: 'none' },
          '& .Mui-selected': { color: theme.palette.primary.main },
        }}
        TabIndicatorProps={{ sx: { height: 2 } }}
      >
        <Tab label="Sections" />
        <Tab label="History" />
      </Tabs>

      {tab === 0 && (
        <Box sx={{ maxHeight: 450, overflow: 'auto', py: 1.5, px: 1.5 }}>
          {sections.length === 0 ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', py: 2 }}>
              No sections yet
            </Typography>
          ) : (
            <Box sx={{ position: 'relative', pl: 2 }}>
              {/* Vertical tracking line */}
              <Box
                sx={{
                  position: 'absolute',
                  left: 6,
                  top: 8,
                  bottom: 8,
                  width: 2,
                  bgcolor: theme.palette.divider,
                  borderRadius: 1,
                }}
              />
              {sections.map((section, idx) => (
                <Box
                  key={idx}
                  onClick={() => onSectionClick(section.index)}
                  sx={{
                    position: 'relative',
                    py: 0.75,
                    pl: 1.5,
                    cursor: 'pointer',
                    borderRadius: 1.5,
                    transition: 'background 0.15s',
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
                  }}
                >
                  {/* Dot on the tracking line */}
                  <Box
                    sx={{
                      position: 'absolute',
                      left: -14,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: section.partName ? theme.palette.primary.main : theme.palette.grey[400],
                      border: `2px solid ${theme.palette.background.paper}`,
                      boxShadow: `0 0 0 2px ${section.partName ? alpha(theme.palette.primary.main, 0.3) : theme.palette.divider}`,
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      fontWeight: 500,
                      lineHeight: 1.3,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      color: 'text.primary',
                    }}
                  >
                    {section.question || `Section ${idx + 1}`}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                    {section.partName ?? 'General'}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}

      {tab === 1 && (
        <Box sx={{ maxHeight: 450, overflow: 'auto', py: 1.5, px: 1.5 }}>
          {versions.length === 0 ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', py: 2 }}>
              No versions yet
            </Typography>
          ) : (
            <Box sx={{ position: 'relative', pl: 2 }}>
              {/* Vertical tracking line */}
              <Box
                sx={{
                  position: 'absolute',
                  left: 6,
                  top: 8,
                  bottom: 8,
                  width: 2,
                  bgcolor: theme.palette.divider,
                  borderRadius: 1,
                }}
              />
              {versions.map((v, idx) => (
                <Box
                  key={v.id ?? idx}
                  sx={{
                    position: 'relative',
                    py: 0.75,
                    pl: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  {/* Dot */}
                  <Box
                    sx={{
                      position: 'absolute',
                      left: -14,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor:
                        v.status === 'ACTIVE' ? theme.palette.success.main
                        : v.status === 'DRAFT' ? theme.palette.warning.main
                        : theme.palette.grey[400],
                      border: `2px solid ${theme.palette.background.paper}`,
                      boxShadow: `0 0 0 2px ${theme.palette.divider}`,
                    }}
                  />
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', lineHeight: 1.3 }}>
                      Version {v.version}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
                      {v.createdAt ? new Date(v.createdAt).toLocaleDateString() : ''}
                    </Typography>
                  </Box>
                  <Chip
                    label={v.status}
                    size="small"
                    color={v.status === 'ACTIVE' ? 'success' : v.status === 'DRAFT' ? 'warning' : 'default'}
                    variant="outlined"
                    sx={{ height: 18, fontSize: '0.6rem', ml: 1 }}
                  />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default SidebarTracker;
